import { useTranslation } from 'next-i18next';
import { getTenantById } from 'tenants';
import { TenantId } from '@tenants/types';
import dataJSON from 'components/header/exp/data.json';

/**
 * Custom hook to get translated header configuration for a given tenant.
 * 
 * This hook encapsulates all the logic for:
 * - Using tenant ID as namespace (when it matches a tenant namespace)
 * - Getting header elements from tenant config or fallbacks
 * - Translating all header elements when tenant has translation support
 * 
 * @param tenantId - The tenant ID to get translated header config for
 * @returns Object containing translated authMenuItems, menuItems, and buttonLabels
 */
export function useTranslatedHeaderConfig(tenantId: TenantId) {
  const tenant = getTenantById(tenantId);
  const { userLinks } = dataJSON;
  
  // Use tenant ID as namespace if it exists in the tenant's namespaces
  const tenantNamespace = tenant.localization?.namespaces?.find(ns => ns === tenantId);
  const { t } = useTranslation(tenantNamespace);
  
  // Get raw menu items from tenant config or fallbacks
  const rawAuthMenuItems = ('header' in tenant.config ? tenant.config.header?.authMenuItems : null) || userLinks;
  const rawMenuItems = ('header' in tenant.config ? tenant.config.header?.menuItems : null) || tenant.theme.menuLinks;
  const rawButtonLabels = ('header' in tenant.config ? tenant.config.header?.buttonLabels : null);
  
  // Check if this tenant has header config (which means it uses translation keys)
  const hasHeaderConfig = 'header' in tenant.config && tenant.config.header;
  const hasTranslations = hasHeaderConfig && tenantNamespace;
  
  // Translate labels when tenant has translation support
  const authMenuItems = rawAuthMenuItems.map(item => ({
    ...item,
    label: t(item.label.key, { defaultValue: item.label.defaultValue }),
  }));
    
  const menuItems = rawMenuItems.map(item => ({
    ...item,
    label: t(item.label.key, { defaultValue: item.label.defaultValue }),
  }));

  // Translate main button copies when available in tenant config
  const mainButtonCopy = hasHeaderConfig 
    ? (hasTranslations ? t(tenant.config.header.mainButtonCopy.key, { defaultValue: tenant.config.header.mainButtonCopy.defaultValue }) : tenant.config.header.mainButtonCopy.defaultValue)
    : undefined;
    
  const authButtonCopy = hasHeaderConfig
    ? (hasTranslations ? t(tenant.config.header.authButtonCopy.key, { defaultValue: tenant.config.header.authButtonCopy.defaultValue }) : tenant.config.header.authButtonCopy.defaultValue)
    : undefined;

  // Translate additional button labels when available in tenant config
  const buttonLabels = hasTranslations && rawButtonLabels ? {
    commercialWaitlist: rawButtonLabels.commercialWaitlist ? t(rawButtonLabels.commercialWaitlist.key, { defaultValue: rawButtonLabels.commercialWaitlist.defaultValue }) : undefined,
    imReady: rawButtonLabels.imReady ? t(rawButtonLabels.imReady.key, { defaultValue: rawButtonLabels.imReady.defaultValue }) : undefined,
    contactUs: rawButtonLabels.contactUs ? t(rawButtonLabels.contactUs.key, { defaultValue: rawButtonLabels.contactUs.defaultValue }) : undefined,
  } : undefined;

  return {
    authMenuItems,
    menuItems,
    mainButtonCopy,
    authButtonCopy,
    buttonLabels,
  };
}
